import {Store} from '@gongxigames/framework/core';
import {DI, ILogic, ILogicBuyFeatureFunctional, inversifyContainer} from '@gongxigames/framework/slot';
import {Action, Bonus, SpinData} from '@gongxigames/framework/slotV2/types/LogicData';
import {Reel} from 'com/gongxigames/math/core/game/Reel';
import {SlotSymbol} from 'com/gongxigames/math/core/game/engine/SlotSymbol';
import {ReelSet} from 'com/gongxigames/math/core/game/ReelSet';

let logic: ILogic | null = null;

/* eslint-disable @typescript-eslint/naming-convention */
// TODO avoid computed and create parser of data just in case response/request
export const reComputeLogicTrigger = Store.signal(1);
const actionNumber = Store.signal(0);
const betId = Store.signal(0);
const logic_spinData = Store.signal(null as never as SpinData);
const logic_symbolIds = Store.computed(() => getStoreLogic().getSymbolIds() as number[][]);
const logic_collapseStepID = Store.signal(0);
const logic_totalBet = Store.computed(() => getStoreLogic().getTotalBet());
const logic_nextAction = Store.signal(0 as Action);
const logic_previousAction = Store.signal(0 as Action);
const logic_previousRoundSpinData = Store.computed(() => getStoreLogic().getPreviousRoundSession() || {} as SpinData);
const logic_buyFeatureBet = Store.signal(0);
/* eslint-enable  @typescript-eslint/naming-convention */

export function getStoreLogic<T extends ILogic & ILogicBuyFeatureFunctional>(): T {
    logic = inversifyContainer.get<T>(DI.Logic);

    return (reComputeLogicTrigger.value && logic) as T;
}

export function resetStoreLogic(): void {
    logic = null;
}

export const logicStore = Store.createStore({
    reComputeLogicTrigger,
    actionNumber,
    betId,
    logic_spinData,
    logic_symbolIds,
    logic_collapseStepID,
    logic_totalBet,
    logic_nextAction,
    logic_previousAction,
    logic_previousRoundSpinData,
    logic_buyFeatureBet,
    // @ts-ignore
    logic_nextActionIsMiniSpin: Store.computed(() => logic_nextAction.value === Action.MINISPIN),
    logic_nextActionIsSelect: Store.computed(() => logic_nextAction.value === Action.SELECT),
    logic_nextActionIsSpin: Store.computed(() => logic_nextAction.value === Action.SPIN),
    logic_nextActionIsFreeSpin: Store.computed(() => logic_nextAction.value === Action.FREESPIN),
    logic_nextActionIsRespin: Store.computed(() => logic_nextAction.value === Action.RESPIN),
    logic_nextActionIsPick: Store.computed(() => logic_nextAction.value === Action.PICK),

    logic_previousSpinRoundWin: Store.signal(0),
    logic_previousActionIsRespin: Store.computed(() => logic_previousAction.value === Action.RESPIN),
    logic_previousActionIsSpin: Store.computed(() => logic_previousAction.value === Action.SPIN),
    logic_previousActionIsFreeSpin: Store.computed(() => logic_previousAction.value === Action.FREESPIN),
    logic_previousActionIsPick: Store.computed(() => logic_previousAction.value === Action.PICK),
    // @ts-ignore
    logic_previousActionIsMiniSpin: Store.computed(() => logic_previousAction.value === Action.MINISPIN),

    // @ts-ignore
    logic_previousRoundIsMiniSpin: Store.computed(() => logic_previousRoundSpinData.value?.action === Action.MINISPIN),
    logic_previousRoundIsRespin: Store.computed(() => logic_previousRoundSpinData.value?.action === Action.RESPIN),

    logic_win: Store.computed(() => getStoreLogic().getWin()),
    logic_totalWin: Store.computed(() => getStoreLogic().getTotalWin()),
    logic_bets: Store.computed(() => getStoreLogic().getBets()),
    logic_bet: Store.computed(() => getStoreLogic().getBet()),
    logic_defaultBet: Store.computed(() => getStoreLogic().getDefaultBet()),
    logic_sessionBet: Store.computed(() => getStoreLogic().getSessionBet()),
    logic_balance: Store.computed(() => getStoreLogic().getBalance()),
    logic_wager: Store.computed(() => getStoreLogic().getWager()),
    logic_betInfo: Store.computed(() => getStoreLogic().getBetInfo()),
    logic_shouldHideBuyFeature: Store.computed(() => getStoreLogic().shouldHideBuyFeature()),
    logic_buyFeatureIsProgressStarted: Store.computed(() => getStoreLogic().getBuyFeature().isProgressStarted),
    logic_buyFeatureMultiplier: Store.computed(() => getStoreLogic().getBuyFeatureMultiplier()),
    logic_canBuyFeatureSpin: Store.computed(() => {
        return getStoreLogic().canBuyFeature() && getStoreLogic().canSpin(getStoreLogic().getBuyFeaturePrice());
    }),
    logic_buyFeaturePrice: Store.computed(() => {
        return logic_buyFeatureBet.value && getStoreLogic().getBuyFeaturePrice();
    }),
    logic_buyFeatureIndependentBet: Store.computed(() => getStoreLogic().getBuyFeature().isIndependentBet),

    logic_freeGamesIndex: Store.computed(() => getStoreLogic().getFreeGamesIndex()),
    logic_freeGamesCount: Store.computed(() => getStoreLogic().getFreeGamesCount()),
    logic_freeGamesTotalCount: Store.computed(() => getStoreLogic().getFreeGamesTotalCount()),
    logic_isLastFreeSpin: Store.computed(() => getStoreLogic().isLastFreeSpin() || false),
    logic_isFreeSpinsWon: Store.computed(() => getStoreLogic().isFreeSpinsWon() || false),
    logic_fireCatcherPrizeCount: Store.computed(() => getStoreLogic().getFirecatcherPrizeCount()),
    logic_fireCatcherRespinCount: Store.computed(() => getStoreLogic().getFirecatcherRespinCount()),
    logic_reTriggerCount: Store.computed(() => getStoreLogic().getRetriggerCount()),
    logic_isRetriggerWon: Store.computed(() => getStoreLogic().isRetriggerWon() || false),

    logic_isBonusFreeGames: Store.computed(() => logic_spinData.value.bonusId === Bonus.FREE_GAMES_BONUS),
    logic_isBonusRespinGames: Store.computed(() => logic_spinData.value.bonusId === Bonus.RESPIN_BONUS),
    logic_freeGamesBonus: Store.computed(() => getStoreLogic().getFreeGamesBonus()),
    logic_isBigWin: Store.computed(() => getStoreLogic().isBigWin() || false),
    logic_isBigTotalWin: Store.computed(() => getStoreLogic().isBigTotalWin() || false),

    logic_sessionForRespinWinningRound: Store.computed(() => (getStoreLogic().getSessionForRespinWinningRound()) as SpinData),
    logic_sessionForFreeSpinsWinningRound: Store.computed(() => (getStoreLogic().getSessionForFreeSpinsWinningRound()) as SpinData),

    logic_reelSet: Store.computed(() => (getStoreLogic().getReelSet()) as ReelSet),
    logic_nextReelSet: Store.computed(() => (getStoreLogic().getNextReelSet()) as ReelSet),
    logic_mainGameReelSet: Store.computed(() => (getStoreLogic().getMainGameReelSet()) as ReelSet),
    logic_reels: Store.computed(() => (getStoreLogic().getReelSet().getReels()) as Reel[]),
    logic_nextReels: Store.computed(() => (getStoreLogic().getNextReelSet().getReels()) as Reel[]),
    logic_mainGameReels: Store.computed(() => (getStoreLogic().getMainGameReelSet().getReels()) as Reel[]),
    logic_isWaysGame: Store.computed(() => getStoreLogic().isWaysGame()),
    logic_isLinesGame: Store.computed(() => getStoreLogic().isLinesGame()),
    logic_lines: Store.computed(() => getStoreLogic().getLines()),
    logic_getSlotDimension: Store.computed(() => getStoreLogic().getSlotDimension()),
    logic_bigWinLimit: Store.computed(() => getStoreLogic().getBigWinLimit()),
    logic_pickedValues: Store.computed(() => getStoreLogic().getPickedValues()),
    logic_getResultsOfHiddenWins: Store.computed(() => getStoreLogic().getResultsOfHiddenWins()),
    logic_getJackpotPayments: Store.computed(() => getStoreLogic().getJackpotPayments()),
    logic_requestPick: Store.signal((cb: Function, pickId: number) => getStoreLogic().pick(cb, pickId)),
    logic_roundInfo: Store.computed(() => getStoreLogic().getRoundInfo()),
    logic_canSpin: Store.computed(() => getStoreLogic().canSpin(logic_totalBet.value)),
    logic_baseWinFromTriggeringRound: Store.computed(() => getStoreLogic().getBaseWinFromTriggeringRound(logic_previousAction.value - 1)),
    logic_isBuyFeatureProgressStarted: Store.computed(() => getStoreLogic?.().getBuyFeature?.().isProgressStarted),

    // TODO need to discuss, maybe we need to come up with something to not perform functions through signals
    //  maybe it can be a boolean updated with some lifecycle actions,
    //  or perhaps we can have some life cycle events so we can update values in the framework and the game when they happen
    logic_getReelSetById: Store.computed(() => (reelSetId: number) => (getStoreLogic().getReelsetByID(reelSetId)) as ReelSet),
    logic_getReelsById: Store.computed(() => (reelSetId: number) => (getStoreLogic().getReelsetByID(reelSetId).getReels()) as Reel[]),
    logic_slotSymbol: Store.computed(() => (symbolId: number) => (getStoreLogic().getSlotSymbol(symbolId)) as SlotSymbol),
    logic_betFactor: Store.computed(() => (symbolId: number | string) => getStoreLogic().getBetFactor(symbolId)),
    logic_isBonusOnFreeGames: Store.computed(() => (bonusId?: number) => getStoreLogic().isBonusOnFreeGames(bonusId)),
    logic_getBonus: Store.computed(() => (bonusId: number) => getStoreLogic().getBonus(bonusId)),
    logic_updateSessionBet: Store.computed(() => () => getStoreLogic().updateSessionBet()),

    // tumble reels
    logic_isAnyCollapseOnSpin: Store.computed(() => getStoreLogic().isAnyCollapseOnSpin()),
    logic_getPaysByStep: Store.computed(() => (collapseStepID: number) => getStoreLogic().getPaysByStep(collapseStepID)),
    logic_getByStepSymbolIds: Store.computed(() => (collapseStepID: number) => getStoreLogic().getByStepSymbolIds(collapseStepID)),
    logic_getByStepDropAmount: Store.computed(() => (collapseStepID: number) => getStoreLogic().getByStepDropAmount(collapseStepID)),
    logic_getByStepReelPositions: Store.computed(() => (collapseStepID: number) => getStoreLogic().getByStepReelPositions(collapseStepID)),
    logic_getByStepBeforeDropSymbolIds: Store.computed(() => (collapseStepID: number) => getStoreLogic().getByStepBeforeDropSymbolIds(collapseStepID)),
    logic_getLastStepSymbolIds: Store.computed(() => getStoreLogic().getLastStepSymbolIds()),
    logic_getAllPays: Store.computed(() => getStoreLogic().getAllPays()),
    logic_isAnyScatterPay: Store.computed(() => getStoreLogic().isAnyScatterPayOnTumbleSpin()),
});